import {
  Ban,
  Calendar,
  CircleDollarSign,
  Eye,
  Mail,
  MousePointer,
  Palmtree,
  Phone,
  Reply,
  TrendingUp,
  User,
} from "lucide-react";
import { Box, Grid, Paper, Skeleton } from "@mui/material";
import React from "react";
import MetricCard from "../dashboard/sequences/SequenceMetricsCard";

// Skeleton component that matches MetricCard dimensions exactly
const MetricCardSkeleton: React.FC = () => (
  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
    <Box display="flex" alignItems="center" mb={1}>
      <Skeleton variant="circular" width={24} height={24} />
      <Skeleton variant="text" width={80} height={24} sx={{ ml: 1 }} />
    </Box>
    <Skeleton variant="text" width={60} height={40} />
    <Skeleton variant="text" width={70} height={20} />
  </Paper>
);

export interface VoiceMetrics {
  total_calls?: number;
  calls_completed?: number;
  calls_answered_human?: number;
  calls_answered_machine?: number;
  calls_no_answer?: number;
  total_call_duration?: number;
  avg_call_duration?: number;
  human_answer_rate?: number;
  prospects_engaged?: number;
  engagement_rate?: number;
  meetings_booked_voice?: number;
  meeting_booked_rate?: number;
  voicemails_sent?: number;
  sms_sent?: number;
  sms_delivered?: number;
  sms_delivery_rate?: number;
  outcomes?: Record<string, number>;
}

export interface SequenceMetricsTotals {
  total_executions?: number;
  total_unique_opens?: number;
  total_unique_clicks?: number;
  total_unique_replies?: number;
  total_bookings?: number;
  total_bounces?: number;
  total_ooo?: number;
  total_pending_approvals?: number;
  pipeline_value?: number;
  overall_open_rate?: number;
  overall_click_rate?: number;
  overall_reply_rate?: number;
  overall_booking_rate?: number;
  overall_bounce_rate?: number;
  overall_ooo_rate?: number;
}

interface SequenceMetricsCardsProps {
  totals: SequenceMetricsTotals;
  voiceMetrics?: VoiceMetrics;
  showPendingApprovals?: boolean;
  onPendingApprovalsClick?: () => void;
  customPipelineValue?: number | null;
  pipelineValueTooltip?: string;
  showPipelineValue?: boolean;
  showVoiceMetrics?: boolean;
  loading?: boolean;
}

const SequenceMetricsCards: React.FC<SequenceMetricsCardsProps> = ({
  totals,
  voiceMetrics,
  showPendingApprovals = false,
  onPendingApprovalsClick,
  customPipelineValue,
  pipelineValueTooltip,
  showPipelineValue = true,
  showVoiceMetrics = true,
  loading = false,
}) => {
  // Use custom pipeline value if provided, otherwise use totals.pipeline_value
  const pipelineValue = customPipelineValue !== undefined && customPipelineValue !== null
    ? customPipelineValue
    : (totals.pipeline_value || 0);

  // Check if we have meaningful voice metrics to display
  const hasVoiceData = voiceMetrics && (
    (voiceMetrics.total_calls && voiceMetrics.total_calls > 0) ||
    (voiceMetrics.calls_answered_human && voiceMetrics.calls_answered_human > 0)
  );

  // Calculate total number of cards to show (for skeleton matching)
  // 7 base cards: Total Sent, Opens, Clicks, Replies, Bookings, Bounces, OOO
  // Pending approvals is now shown as secondary info under Total Sent
  // Voice metrics adds up to 3 more cards if data is available
  const voiceCardCount = hasVoiceData && showVoiceMetrics ? 3 : 0;
  const cardCount = 7 + (showPipelineValue ? 1 : 0) + voiceCardCount;

  if (loading) {
    return (
      <Grid container spacing={1} mb={2}>
        {[...Array(cardCount)].map((_, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <MetricCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} mb={2}>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<Mail size={24} />}
          title="Total Sent"
          value={totals.total_executions || 0}
          secondaryValue={showPendingApprovals ? (totals.total_pending_approvals || 0) : undefined}
          secondaryLabel={showPendingApprovals ? "pending" : undefined}
          secondaryButtonText={showPendingApprovals && (totals.total_pending_approvals || 0) > 0 ? "View" : undefined}
          secondaryButtonAction={onPendingApprovalsClick}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<Eye size={24} />}
          title="Unique Opens"
          value={totals.total_unique_opens || 0}
          percentage={totals.overall_open_rate || 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<MousePointer size={24} />}
          title="Unique Clicks"
          value={totals.total_unique_clicks || 0}
          percentage={totals.overall_click_rate || 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<Reply size={24} />}
          title="Unique Replies"
          value={totals.total_unique_replies || 0}
          percentage={totals.overall_reply_rate || 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<Calendar size={24} />}
          title="Meeting Bookings"
          value={totals.total_bookings || 0}
          percentage={totals.overall_booking_rate || 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<Ban size={24} />}
          title="Bounces"
          value={totals.total_bounces || 0}
          percentage={totals.overall_bounce_rate || 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={3}>
        <MetricCard
          icon={<Palmtree size={24} />}
          title="Out of Office"
          value={totals.total_ooo || 0}
          percentage={totals.overall_ooo_rate || 0}
          tooltip="Contacts who replied with an out-of-office message"
        />
      </Grid>
      {showPipelineValue && (
        <Grid item xs={6} sm={4} md={3}>
          <MetricCard
            icon={<CircleDollarSign size={24} />}
            title="Pipeline Value"
            value={`$${pipelineValue.toLocaleString()}`}
            tooltip={pipelineValueTooltip}
          />
        </Grid>
      )}
      {/* Voice/Call Metrics - only show if there's meaningful voice data */}
      {hasVoiceData && showVoiceMetrics && (
        <>
          <Grid item xs={6} sm={4} md={3}>
            <MetricCard
              icon={<Phone size={24} />}
              title="Voice Calls"
              value={voiceMetrics.total_calls || 0}
              percentage={voiceMetrics.human_answer_rate || 0}
              tooltip="Total voice calls made, with human answer rate"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <MetricCard
              icon={<User size={24} />}
              title="Live Conversations"
              value={voiceMetrics.calls_answered_human || 0}
              percentage={voiceMetrics.engagement_rate || 0}
              tooltip="Calls answered by humans, with engagement rate"
            />
          </Grid>
          {(voiceMetrics.meetings_booked_voice || 0) > 0 && (
            <Grid item xs={6} sm={4} md={3}>
              <MetricCard
                icon={<TrendingUp size={24} />}
                title="Meetings (Voice)"
                value={voiceMetrics.meetings_booked_voice || 0}
                percentage={voiceMetrics.meeting_booked_rate || 0}
                tooltip="Meetings booked from voice conversations"
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default SequenceMetricsCards;
